// ./src/styles/theme/colors.ts
import { MantineColorsTuple } from "@mantine/core";

// ============================================
// CarExpenses Brand Colors
// Automotive meets finance - trust, precision, motion
// ============================================

// Brand - Deep Forest Green
// Conveys: Trust, financial stability, eco-conscious driving, reliability
export const brand: MantineColorsTuple = [
  "#f0fdf4", // 0 - Lightest backgrounds, subtle highlights
  "#dcfce7", // 1 - Very light hover states
  "#bbf7d0", // 2 - Light secondary backgrounds
  "#86efac", // 3 - Medium light borders, dividers
  "#4ade80", // 4 - Medium secondary elements
  "#22c55e", // 5 - Bright green for positive states
  "#16a34a", // 6 - Primary brand color - main CTAs
  "#15803d", // 7 - Darker hover on primary
  "#166534", // 8 - Dark active states, headings
  "#14532d", // 9 - Darkest, near-black green
];

// Accent - Amber/Gold
// Dashboard indicators, attention states, premium feel
export const accent: MantineColorsTuple = [
  "#fffbeb", // 0
  "#fef3c7", // 1
  "#fde68a", // 2
  "#fcd34d", // 3
  "#fbbf24", // 4 - Main accent
  "#f59e0b", // 5
  "#d97706", // 6
  "#b45309", // 7
  "#92400e", // 8
  "#78350f", // 9
];

// Road - Slate Blue-Gray
// Asphalt, highways, professional undertone
export const road: MantineColorsTuple = [
  "#f8fafc", // 0
  "#f1f5f9", // 1
  "#e2e8f0", // 2
  "#cbd5e1", // 3
  "#94a3b8", // 4
  "#64748b", // 5 - Main road gray
  "#475569", // 6
  "#334155", // 7
  "#1e293b", // 8
  "#0f172a", // 9
];

// Fuel - Petrol Blue
// Fuel tracking, energy, motion
export const fuel: MantineColorsTuple = [
  "#ecfeff", // 0
  "#cffafe", // 1
  "#a5f3fc", // 2
  "#67e8f9", // 3
  "#22d3ee", // 4
  "#06b6d4", // 5 - Main fuel cyan
  "#0891b2", // 6
  "#0e7490", // 7
  "#155e75", // 8
  "#164e63", // 9
];

// Success - Emerald
// Paid expenses, completed maintenance, positive states
export const success: MantineColorsTuple = [
  "#ecfdf5", // 0
  "#d1fae5", // 1
  "#a7f3d0", // 2
  "#6ee7b7", // 3
  "#34d399", // 4
  "#10b981", // 5 - Main success
  "#059669", // 6
  "#047857", // 7
  "#065f46", // 8
  "#064e3b", // 9
];

// Error - Engine Red
// Overdue maintenance, warnings, negative states
export const error: MantineColorsTuple = [
  "#fef2f2", // 0
  "#fee2e2", // 1
  "#fecaca", // 2
  "#fca5a5", // 3
  "#f87171", // 4
  "#ef4444", // 5 - Main error
  "#dc2626", // 6
  "#b91c1c", // 7
  "#991b1b", // 8
  "#7f1d1d", // 9
];

// Warning - Orange (Check Engine Light)
// Upcoming maintenance, moderate alerts
export const warning: MantineColorsTuple = [
  "#fff7ed", // 0
  "#ffedd5", // 1
  "#fed7aa", // 2
  "#fdba74", // 3
  "#fb923c", // 4
  "#f97316", // 5 - Main warning
  "#ea580c", // 6
  "#c2410c", // 7
  "#9a3412", // 8
  "#7c2d12", // 9
];

// Neutral - Cool Gray
// Professional, clean UI backgrounds
export const neutral: MantineColorsTuple = [
  "#fafafa", // 0 - Off-white backgrounds
  "#f4f4f5", // 1 - Light gray backgrounds
  "#e4e4e7", // 2 - Borders, dividers
  "#d4d4d8", // 3 - Disabled states
  "#a1a1aa", // 4 - Placeholder text
  "#71717a", // 5 - Secondary text
  "#52525b", // 6 - Body text
  "#3f3f46", // 7 - Dark text
  "#27272a", // 8 - Headings
  "#18181b", // 9 - Near black
];

// ============================================
// Semantic Color Aliases
// Use these for consistent meaning across the app
// ============================================
export const semanticColors = {
  // Expense categories
  maintenance: brand[6],
  fuelExpense: fuel[5],
  parking: accent[5],
  fines: error[5],
  carWash: fuel[3],
  repairs: warning[6],
  insurance: road[6],
  taxes: road[7],

  // Status indicators
  paid: success[5],
  pending: warning[5],
  overdue: error[5],
  scheduled: accent[4],

  // Vehicle states
  vehicleActive: success[5],
  vehicleInactive: neutral[4],
  vehicleSold: neutral[5],

  // Maintenance alerts
  maintenanceDue: warning[5],
  maintenanceOverdue: error[5],
  maintenanceCompleted: success[5],

  // Fuel efficiency
  efficiencyGood: success[5],
  efficiencyAverage: accent[5],
  efficiencyPoor: error[5],

  // General UI
  info: fuel[5],
  link: brand[6],
  linkHover: brand[7],
  highlight: accent[3],
} as const;

// ============================================
// Background Colors
// ============================================
export const backgrounds = {
  primary: "#ffffff",
  secondary: neutral[0],
  tertiary: neutral[1],
  brand: brand[0],
  brandDark: brand[9],
  accent: accent[0],
  dashboard: road[9],
} as const;

// ============================================
// Text Colors
// ============================================
export const textColors = {
  primary: neutral[8],
  secondary: neutral[5],
  dimmed: neutral[4],
  inverse: "#ffffff",
  brand: brand[7],
  accent: accent[6],
  muted: road[5],
} as const;

// ============================================
// Border Colors
// ============================================
export const borderColors = {
  light: neutral[2],
  medium: neutral[3],
  dark: neutral[4],
  brand: brand[3],
  accent: accent[3],
  subtle: road[2],
} as const;

// ============================================
// Gradients
// For hero sections, dashboard cards, premium elements
// ============================================
export const gradients = {
  // Primary brand gradient - forest depth
  primary: "linear-gradient(135deg, #166534 0%, #22c55e 100%)",

  // Hero - dark dashboard feel
  hero: "linear-gradient(135deg, #14532d 0%, #166534 50%, #22c55e 100%)",

  // Dashboard card gradient
  dashboard: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",

  // Fuel/energy gradient
  fuel: "linear-gradient(135deg, #0e7490 0%, #22d3ee 100%)",

  // Amber accent gradient
  accent: "linear-gradient(135deg, #d97706 0%, #fbbf24 100%)",

  // Subtle background gradient
  subtle: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)",

  // Premium dark gradient
  dark: "linear-gradient(135deg, #0f172a 0%, #14532d 100%)",

  // Stats card gradient
  stats: "linear-gradient(135deg, #166534 0%, #0e7490 100%)",
} as const;

// ============================================
// Expense Category Colors
// For charts and visual categorization
// ============================================
export const expenseColors = {
  maintenance: "#16a34a", // Forest green
  fuel: "#06b6d4", // Cyan
  parking: "#f59e0b", // Amber
  fines: "#ef4444", // Red
  carWash: "#22d3ee", // Light cyan
  repairs: "#ea580c", // Orange
  insurance: "#64748b", // Slate
  taxes: "#475569", // Dark slate
  tolls: "#8b5cf6", // Purple
  accessories: "#ec4899", // Pink
  other: "#a1a1aa", // Gray
} as const;

// ============================================
// Chart Colors
// For data visualization
// ============================================
export const chartColors = {
  primary: [
    "#166534", // Forest green
    "#06b6d4", // Cyan
    "#f59e0b", // Amber
    "#ef4444", // Red
    "#8b5cf6", // Purple
    "#ec4899", // Pink
    "#22c55e", // Light green
    "#64748b", // Slate
  ],
  sequential: [
    "#f0fdf4",
    "#dcfce7",
    "#bbf7d0",
    "#86efac",
    "#4ade80",
    "#22c55e",
    "#16a34a",
    "#15803d",
    "#166534",
    "#14532d",
  ],
} as const;

// ============================================
// Color Palette Export
// ============================================
export const colorPalette = {
  brand,
  accent,
  road,
  fuel,
  success,
  error,
  warning,
  neutral,
  semantic: semanticColors,
  backgrounds,
  text: textColors,
  borders: borderColors,
  gradients,
  expenses: expenseColors,
  charts: chartColors,
} as const;
